"use client";
import React, { forwardRef, useCallback } from "react";
import { useTimescape, type Options } from "timescape-fix/react";

import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
// @source: https://github.com/dan-lee/timescape?tab=readme-ov-file

const timePickerInputBase =
    "p-1 inline tabular-nums h-fit border-none outline-none select-none content-box caret-transparent rounded-sm min-w-8 text-center focus:bg-foreground/20 focus-visible:ring-0 focus-visible:outline-none";
const timePickerSeparatorBase = "text-xs text-gray-400";

type DateFormat = "days" | "months" | "years";
type TimeFormat = "hours" | "minutes" | "seconds" | "milliseconds" | "am/pm";

type DateTimeArray<T extends DateFormat | TimeFormat> = T[];

export type DateTimeFormat = {
    dateFormat?: DateTimeArray<DateFormat>;
    timeFormat?: DateTimeArray<TimeFormat>;
}

const DEFAULTS = {
    dateFormat: ["months", "days", "years"],
    timeFormat: ["hours", "minutes", "am/pm"],
} as DateTimeFormat;

type TimescapeReturn = ReturnType<typeof useTimescape>;
type InputPlaceholders = Record<DateFormat | TimeFormat, string>;
const INPUT_PLACEHOLDERS: InputPlaceholders = {
    months: "MM",
    days: "DD",
    years: "YYYY",
    hours: "HH",
    minutes: "MM",
    seconds: "SS",
    "am/pm": "AM/PM",
    milliseconds: "ms",
};

/**
 * Date time picker Docs: {@link: https://shadcn-extension.vercel.app/docs/otp-input}
 */

const DatetimeGrid = forwardRef<
    HTMLDivElement,
    {
        format: DateTimeFormat;
        className?: string;
        timescape: Pick<TimescapeReturn, "getRootProps" | "getInputProps">;
        placeholders: InputPlaceholders;
        disabled?: boolean;
    }
>(
    (
        {
            format,
            className,
            timescape,
            placeholders,
            disabled
        }: {
            format: DateTimeFormat;
            className?: string;
            timescape: Pick<TimescapeReturn, "getRootProps" | "getInputProps">;
            placeholders: InputPlaceholders;
            disabled?: boolean;
        },
        ref,
    ) => {
        return (
            <div
                className={cn(
                    "flex items-center w-fit p-1 border-2",
                    className,
                    "border-input rounded-md gap-1 selection:bg-transparent selection:text-foreground",
                )}
                {...timescape.getRootProps()}
                ref={ref}
            >
                {format
                    ? <>
                        {format.dateFormat?.map((unit, i) => (
                            <React.Fragment key={unit}>
                                <Input
                                    className={cn(timePickerInputBase, "min-w-[30px]", {
                                        "min-w-[60px]": unit === "years",
                                    })}
                                    {...timescape.getInputProps(unit)}
                                    placeholder={placeholders[unit]}
                                    disabled={disabled}
                                />
                                {i < format.dateFormat!.length - 1 ? (
                                    <span className={timePickerSeparatorBase}>/</span>
                                ) : null}
                            </React.Fragment>
                        ))}
                        {(!!format?.dateFormat?.length && !!format?.timeFormat?.length) && (
                            <span
                                className={cn(
                                    timePickerSeparatorBase,
                                    "opacity-30 text-xl",
                                )}
                            >
                                |
                            </span>
                        )}
                        {format?.timeFormat?.map((unit, i) => (
                            <React.Fragment key={unit}>
                                <Input
                                    className={cn(timePickerInputBase, "min-w-[30px]", {
                                        "bg-foreground/15": unit === "am/pm",
                                        "min-w-[60px]": unit === "milliseconds",
                                    })}
                                    {...timescape.getInputProps(unit)}
                                    placeholder={placeholders[unit]}
                                />
                                {(i < format.timeFormat!.length - 1) ? (
                                    <span className={timePickerSeparatorBase}>:</span>
                                ) : null}
                            </React.Fragment>
                        ))}
                    </>
                    : null}

            </div>
        );
    },
);

DatetimeGrid.displayName = "DatetimeGrid";

interface DateTimeInput {
    value?: Date;
    format: DateTimeFormat;
    placeholders?: InputPlaceholders;
    onChange?: Options["onChangeDate"];
    dtOptions?: Options;
    className?: string;
}

const DEFAULT_TS_OPTIONS = {
    date: new Date(),
    hour12: true,
};
export const DatetimePicker = forwardRef<HTMLDivElement, DateTimeInput>(
    (
        {
            value = new Date(),
            format = DEFAULTS,
            placeholders,
            dtOptions = DEFAULT_TS_OPTIONS,
            onChange,
            className,
        },
        ref,
    ) => {
        const handleDateChange = useCallback(
            (nextDate: Date | undefined) => {
                onChange ? onChange(nextDate) : console.log(nextDate);
            },
            [onChange],
        );
        const timescape = useTimescape({
            date: value,
            onChangeDate: handleDateChange,
            ...dtOptions,
        });
        return (
            <DatetimeGrid
                format={format}
                className={className}
                timescape={timescape}
                placeholders={placeholders ?? INPUT_PLACEHOLDERS}
                ref={ref}
            />
        );
    },
);

DatetimePicker.displayName = "DatetimePicker";
